import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Heading,
  Input,
  LoadingSpinner,
  SortableTableHeader,
  Text,
  Tooltip,
} from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { usePermission } from '../hooks/usePermissions'
import { useUserLogs } from '../hooks/useUserLogs'
import { modulesService } from '../services/modules'
import { cctBuscarDadosService } from '../services/cctBuscarDados'
import {
  assertValidCctRootDirectory,
  authorizeRootDirectory,
  clearAuthorizedRootDirectory,
  getAuthorizedRootDirectory,
  isFileSystemAccessSupported,
  listZipFilesForIdScgp,
} from '../utils/cctBuscarDadosPasta'
import { extractDocumentoFromZip } from '../utils/cctBuscarDadosExtractZip'
import { downloadXlsx } from '../utils/downloadXlsx'
import { CheckboxMultiSelect } from './Qualidade/CheckboxMultiSelect'
import type {
  CctBuscarDadosConvencao,
  CctBuscarDadosParameter,
  CctBuscarDadosProcessItem,
  CctBuscarDadosProcessItemStatus,
  CctBuscarDadosProfissaoItem,
  CctBuscarDadosResultado,
  CctBuscarDadosSalario,
} from '../types/cctBuscarDados'
import {
  CCT_BUSCAR_DADOS_MODULE_NAME,
  CCT_BUSCAR_DADOS_PARAM_DIRETORIO,
  CCT_BUSCAR_DADOS_PARAM_WEBHOOK,
  parseCctBuscarDadosProfissoes,
} from '../types/cctBuscarDados'
import { ArrowDownTrayIcon, ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'

type ActiveTab = 'busca' | 'historico' | 'parametros'
type ConvencaoSortKey =
  | 'id_scgp'
  | 'cnpj_entidade_laboral'
  | 'cnpj_entidade_patronal'
  | 'cod_categoria'
  | 'status'
  | 'id'
type HistoricoSortKey =
  | 'created_at'
  | 'id_scgp'
  | 'arquivo_nome'
  | 'status'
  | 'status_convencao'
  | 'categoria'
  | 'profissao'

interface HistoricoLinha {
  key: string
  resultadoId: string
  id_scgp: number
  arquivo_nome: string | null
  created_at: string
  status: string
  statusConvencao: string
  erro: string | null
  categoria: string
  profissao: string
  salarios: CctBuscarDadosSalario[]
}

const MODULE_NAME = CCT_BUSCAR_DADOS_MODULE_NAME
const ITEMS_PER_PAGE = 20

const tabClass = (active: boolean) =>
  `py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
    active
      ? 'border-primary-600 text-primary-600 dark:text-primary-400'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
  }`

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return value
  }
}

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message
  const maybe = error as { message?: string; code?: string }
  if (maybe?.message) return maybe.message
  if (maybe?.code) return `Erro (${maybe.code})`
  return 'Erro inesperado ao processar solicitação.'
}

const statusProcessLabel = (status: CctBuscarDadosProcessItemStatus): string => {
  switch (status) {
    case 'aguardando':
      return 'Aguardando'
    case 'lendo_pasta':
      return 'Lendo pasta'
    case 'enviando':
      return 'Enviando'
    case 'sucesso':
      return 'Sucesso'
    case 'pasta_nao_encontrada':
      return 'Pasta não encontrada'
    case 'sem_zip':
      return 'Sem zip'
    case 'erro':
      return 'Erro'
    case 'cancelado':
      return 'Cancelado'
    default:
      return status
  }
}

const statusProcessClass = (status: CctBuscarDadosProcessItemStatus | string): string => {
  switch (status) {
    case 'sucesso':
      return 'text-green-600 dark:text-green-400'
    case 'erro':
    case 'pasta_nao_encontrada':
    case 'sem_zip':
      return 'text-red-600 dark:text-red-400'
    case 'enviando':
    case 'lendo_pasta':
      return 'text-blue-600 dark:text-blue-400'
    case 'cancelado':
      return 'text-gray-500 dark:text-gray-400'
    default:
      return 'text-gray-700 dark:text-gray-300'
  }
}

const PaginationControls: React.FC<{
  currentPage: number
  totalPages: number
  startIndex: number
  endIndex: number
  total: number
  onPageChange: (page: number) => void
  onPrevious: () => void
  onNext: () => void
}> = ({ currentPage, totalPages, startIndex, endIndex, total, onPageChange, onPrevious, onNext }) => {
  if (totalPages <= 1) return null
  return (
    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <Text size="sm" className="text-gray-700 dark:text-gray-300">
          Mostrando {startIndex + 1} a {Math.min(endIndex, total)} de {total} registros
        </Text>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={onPrevious}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Anterior
          </button>
          <div className="flex space-x-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNumber: number
              if (totalPages <= 5) pageNumber = i + 1
              else if (currentPage <= 3) pageNumber = i + 1
              else if (currentPage >= totalPages - 2) pageNumber = totalPages - 4 + i
              else pageNumber = currentPage - 2 + i
              return (
                <button
                  key={pageNumber}
                  type="button"
                  onClick={() => onPageChange(pageNumber)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    currentPage === pageNumber
                      ? 'bg-primary-600 text-white'
                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  {pageNumber}
                </button>
              )
            })}
          </div>
          <button
            type="button"
            onClick={onNext}
            disabled={currentPage === totalPages}
            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Próximo
          </button>
        </div>
      </div>
    </div>
  )
}

const CCTBuscarDados: React.FC = () => {
  const { logCreate, logUpdate } = useUserLogs()
  const { hasPermission: canInsert, loading: loadingInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate, loading: loadingUpdate } = usePermission(MODULE_NAME, 'update')

  const [moduleTitle, setModuleTitle] = useState('CCTs • Buscar dados')
  const [moduleDescription, setModuleDescription] = useState(
    'Filtrar convenções e processar documentos via n8n',
  )
  const [activeTab, setActiveTab] = useState<ActiveTab>('busca')

  const [loadingConvencoes, setLoadingConvencoes] = useState(true)
  const [loadingParameters, setLoadingParameters] = useState(false)
  const [loadingResultados, setLoadingResultados] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [pageError, setPageError] = useState<string | null>(null)
  const [refreshError, setRefreshError] = useState<string | null>(null)

  const [allConvencoes, setAllConvencoes] = useState<CctBuscarDadosConvencao[]>([])
  const [categoriasMap, setCategoriasMap] = useState<Map<string, string>>(new Map())

  const [filtroIdScgp, setFiltroIdScgp] = useState<string[]>([])
  const [filtroLaboral, setFiltroLaboral] = useState<string[]>([])
  const [filtroPatronal, setFiltroPatronal] = useState<string[]>([])
  const [filtroCategorias, setFiltroCategorias] = useState<string[]>([])
  const [filtroStatuses, setFiltroStatuses] = useState<string[]>([])

  const [sortBy, setSortBy] = useState<ConvencaoSortKey>('id_scgp')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [currentPage, setCurrentPage] = useState(1)

  const [resultados, setResultados] = useState<CctBuscarDadosResultado[]>([])
  const [idsJaProcessados, setIdsJaProcessados] = useState<Set<number>>(new Set())
  const [histFiltroIdScgp, setHistFiltroIdScgp] = useState<string[]>([])
  const [histFiltroStatus, setHistFiltroStatus] = useState<string[]>([])
  const [histFiltroProfissao, setHistFiltroProfissao] = useState('')
  const [historicoSortBy, setHistoricoSortBy] = useState<HistoricoSortKey>('created_at')
  const [historicoSortOrder, setHistoricoSortOrder] = useState<'asc' | 'desc'>('desc')
  const [historicoPage, setHistoricoPage] = useState(1)
  const [exportingExcel, setExportingExcel] = useState(false)

  const [parameters, setParameters] = useState<CctBuscarDadosParameter[]>([])
  const [editingParameter, setEditingParameter] = useState<CctBuscarDadosParameter | null>(null)
  const [parameterEditValue, setParameterEditValue] = useState('')
  const [isParameterModalOpen, setIsParameterModalOpen] = useState(false)
  const [savingParameter, setSavingParameter] = useState(false)
  const [parameterFormError, setParameterFormError] = useState<string | null>(null)

  const [pastaAutorizada, setPastaAutorizada] = useState(false)
  const [pastaNome, setPastaNome] = useState<string | null>(null)
  const [autorizandoPasta, setAutorizandoPasta] = useState(false)

  const [processando, setProcessando] = useState(false)
  const [processItems, setProcessItems] = useState<CctBuscarDadosProcessItem[]>([])
  const cancelRef = useRef(false)
  const processingLockRef = useRef(false)
  const runIdRef = useRef(0)

  useEffect(() => {
    const fetchModuleInfo = async () => {
      try {
        const result = await modulesService.getByName(MODULE_NAME)
        if (result.success && result.data) {
          if (result.data.display_name) setModuleTitle(result.data.display_name)
          if (result.data.description) setModuleDescription(result.data.description)
        }
      } catch (err) {
        console.error('Erro ao buscar informações do módulo:', err)
      }
    }
    void fetchModuleInfo()
  }, [])

  const loadCategorias = useCallback(async () => {
    const cats = await cctBuscarDadosService.listCategorias()
    const map = new Map<string, string>()
    cats.forEach((c) => {
      if (c.id != null) map.set(String(c.id), c.nome || '—')
    })
    setCategoriasMap(map)
  }, [])

  const loadConvencoes = useCallback(async (options?: { silent?: boolean }) => {
    const silent = Boolean(options?.silent)
    if (silent) {
      setIsRefreshing(true)
      setRefreshError(null)
    } else {
      setLoadingConvencoes(true)
      setPageError(null)
    }
    try {
      const data = await cctBuscarDadosService.listAllConvencoesForFilters()
      setAllConvencoes(data)
    } catch (err) {
      const msg = getErrorMessage(err)
      if (silent) setRefreshError(msg)
      else setPageError(msg)
    } finally {
      if (silent) setIsRefreshing(false)
      else setLoadingConvencoes(false)
    }
  }, [])

  const loadParameters = useCallback(async () => {
    setLoadingParameters(true)
    try {
      const data = await cctBuscarDadosService.listParameters()
      setParameters(data)
    } catch (err) {
      setPageError(getErrorMessage(err))
    } finally {
      setLoadingParameters(false)
    }
  }, [])

  const loadResultados = useCallback(async () => {
    setLoadingResultados(true)
    try {
      const [data, idsProcessados] = await Promise.all([
        cctBuscarDadosService.listResultados(0),
        cctBuscarDadosService.listIdScgpsJaProcessados(),
      ])
      setResultados(data)
      setIdsJaProcessados(idsProcessados)
    } catch (err) {
      console.error('Erro ao carregar resultados:', err)
    } finally {
      setLoadingResultados(false)
    }
  }, [])

  const refreshPastaAuth = useCallback(async () => {
    try {
      const handle = await getAuthorizedRootDirectory()
      if (handle) {
        setPastaAutorizada(true)
        setPastaNome(handle.name)
      } else {
        setPastaAutorizada(false)
        setPastaNome(null)
      }
    } catch {
      setPastaAutorizada(false)
      setPastaNome(null)
    }
  }, [])

  useEffect(() => {
    void loadCategorias()
    void loadConvencoes()
    void loadParameters()
    void loadResultados()
    void refreshPastaAuth()
  }, [loadCategorias, loadConvencoes, loadParameters, loadResultados, refreshPastaAuth])

  useEffect(() => {
    if (!loadingInsert && !canInsert && activeTab === 'busca') {
      setActiveTab('historico')
      return
    }
    if (!loadingUpdate && !canUpdate && activeTab === 'parametros') {
      setActiveTab(canInsert ? 'busca' : 'historico')
      return
    }
    if (activeTab === 'parametros') {
      void loadParameters()
      void refreshPastaAuth()
    }
    if (activeTab === 'historico') {
      void loadResultados()
    }
  }, [
    activeTab,
    canInsert,
    canUpdate,
    loadingInsert,
    loadingUpdate,
    loadParameters,
    refreshPastaAuth,
    loadResultados,
  ])

  const idScgpOptions = useMemo(() => {
    const set = new Set<string>()
    allConvencoes.forEach((c) => {
      if (c.id_scgp != null) set.add(String(c.id_scgp))
    })
    return Array.from(set)
      .sort((a, b) => Number(a) - Number(b))
      .map((v) => ({ value: v, label: v }))
  }, [allConvencoes])

  const laboralOptions = useMemo(() => {
    const set = new Set<string>()
    allConvencoes.forEach((c) => {
      if (c.cnpj_entidade_laboral?.trim()) set.add(c.cnpj_entidade_laboral.trim())
    })
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
      .map((v) => ({ value: v, label: v }))
  }, [allConvencoes])

  const patronalOptions = useMemo(() => {
    const set = new Set<string>()
    allConvencoes.forEach((c) => {
      if (c.cnpj_entidade_patronal?.trim()) set.add(c.cnpj_entidade_patronal.trim())
    })
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
      .map((v) => ({ value: v, label: v }))
  }, [allConvencoes])

  const categoriaOptions = useMemo(() => {
    if (categoriasMap.size > 0) {
      return Array.from(categoriasMap.entries())
        .sort((a, b) => a[1].localeCompare(b[1], 'pt-BR'))
        .map(([value, nome]) => ({
          value,
          label: nome && nome !== '—' ? `${value} — ${nome}` : value,
        }))
    }
    const set = new Set<string>()
    allConvencoes.forEach((c) => {
      if (c.cod_categoria != null && String(c.cod_categoria).trim() !== '') {
        set.add(String(c.cod_categoria))
      }
    })
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
      .map((v) => ({ value: v, label: v }))
  }, [categoriasMap, allConvencoes])

  const statusOptions = useMemo(() => {
    const set = new Set<string>()
    allConvencoes.forEach((c) => {
      const s = (c.status || '').trim()
      if (s) set.add(s)
    })
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
      .map((v) => ({ value: v, label: v }))
  }, [allConvencoes])

  const convencoesFiltradas = useMemo(() => {
    return allConvencoes.filter((item) => {
      const matchId =
        filtroIdScgp.length === 0 ||
        (item.id_scgp != null && filtroIdScgp.includes(String(item.id_scgp)))
      const matchLaboral =
        filtroLaboral.length === 0 ||
        (!!item.cnpj_entidade_laboral &&
          filtroLaboral.includes(item.cnpj_entidade_laboral.trim()))
      const matchPatronal =
        filtroPatronal.length === 0 ||
        (!!item.cnpj_entidade_patronal &&
          filtroPatronal.includes(item.cnpj_entidade_patronal.trim()))
      const itemCod = item.cod_categoria != null ? String(item.cod_categoria) : ''
      const matchCat =
        filtroCategorias.length === 0 ||
        (itemCod !== '' && filtroCategorias.includes(itemCod))
      const itemStatus = (item.status || '').trim()
      const matchStatus =
        filtroStatuses.length === 0 ||
        filtroStatuses.some((s) => s.trim().toLowerCase() === itemStatus.toLowerCase())
      return matchId && matchLaboral && matchPatronal && matchCat && matchStatus
    })
  }, [allConvencoes, filtroIdScgp, filtroLaboral, filtroPatronal, filtroCategorias, filtroStatuses])

  const convencoesOrdenadas = useMemo(() => {
    const getSortValue = (item: CctBuscarDadosConvencao, key: ConvencaoSortKey): string | number => {
      switch (key) {
        case 'id_scgp':
          return item.id_scgp ?? Number.POSITIVE_INFINITY
        case 'cnpj_entidade_laboral':
          return (item.cnpj_entidade_laboral || '').toLowerCase()
        case 'cnpj_entidade_patronal':
          return (item.cnpj_entidade_patronal || '').toLowerCase()
        case 'cod_categoria':
          return (categoriasMap.get(String(item.cod_categoria ?? '')) || item.cod_categoria || '').toLowerCase()
        case 'status':
          return (item.status || '').toLowerCase()
        case 'id':
          return item.id
        default:
          return ''
      }
    }
    return [...convencoesFiltradas].sort((a, b) => {
      const aVal = getSortValue(a, sortBy)
      const bVal = getSortValue(b, sortBy)
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1
        if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1
        return 0
      }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [convencoesFiltradas, sortBy, sortOrder, categoriasMap])

  const totalPages = Math.ceil(convencoesOrdenadas.length / ITEMS_PER_PAGE) || 1
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const convencoesPaginadas = convencoesOrdenadas.slice(startIndex, endIndex)

  useEffect(() => {
    setCurrentPage(1)
  }, [filtroIdScgp, filtroLaboral, filtroPatronal, filtroCategorias, filtroStatuses])

  const idsCandidatos = useMemo(() => {
    const map = new Map<number, number[]>()
    for (const c of convencoesFiltradas) {
      if (c.id_scgp == null) continue
      const list = map.get(c.id_scgp) ?? []
      list.push(c.id)
      map.set(c.id_scgp, list)
    }
    return Array.from(map.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([idScgp, convencaoIds]) => ({ idScgp, convencaoIds }))
  }, [convencoesFiltradas])

  const idsJaProcessadosCount = useMemo(
    () => idsCandidatos.filter(({ idScgp }) => idsJaProcessados.has(idScgp)).length,
    [idsCandidatos, idsJaProcessados],
  )

  const idsParaProcessar = useMemo(
    () => idsCandidatos.filter(({ idScgp }) => !idsJaProcessados.has(idScgp)),
    [idsCandidatos, idsJaProcessados],
  )

  const semIdScgpCount = useMemo(
    () => convencoesFiltradas.filter((c) => c.id_scgp == null).length,
    [convencoesFiltradas],
  )

  const histIdScgpOptions = useMemo(() => {
    const set = new Set<string>()
    resultados.forEach((r) => {
      if (Number.isFinite(r.id_scgp)) set.add(String(r.id_scgp))
    })
    return Array.from(set)
      .sort((a, b) => Number(a) - Number(b))
      .map((v) => ({ value: v, label: v }))
  }, [resultados])

  const histStatusOptions = useMemo(() => {
    const set = new Set<string>()
    allConvencoes.forEach((c) => {
      const s = (c.status || '').trim()
      if (s) set.add(s)
    })
    return Array.from(set)
      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
      .map((v) => ({ value: v, label: v }))
  }, [allConvencoes])

  const categoriaPorIdScgp = useMemo(() => {
    const map = new Map<number, string>()
    for (const c of allConvencoes) {
      if (c.id_scgp == null || map.has(c.id_scgp)) continue
      if (c.cod_categoria == null || String(c.cod_categoria).trim() === '') continue
      map.set(c.id_scgp, String(c.cod_categoria))
    }
    return map
  }, [allConvencoes])

  const categoriaPorConvencaoId = useMemo(() => {
    const map = new Map<number, string>()
    for (const c of allConvencoes) {
      if (c.cod_categoria == null || String(c.cod_categoria).trim() === '') continue
      map.set(c.id, String(c.cod_categoria))
    }
    return map
  }, [allConvencoes])

  const statusConvencaoPorIdScgp = useMemo(() => {
    const map = new Map<number, string>()
    for (const c of allConvencoes) {
      if (c.id_scgp == null || map.has(c.id_scgp)) continue
      const s = (c.status || '').trim()
      if (!s) continue
      map.set(c.id_scgp, s)
    }
    return map
  }, [allConvencoes])

  const statusConvencaoPorConvencaoId = useMemo(() => {
    const map = new Map<number, string>()
    for (const c of allConvencoes) {
      const s = (c.status || '').trim()
      if (!s) continue
      map.set(c.id, s)
    }
    return map
  }, [allConvencoes])

  const historicoLinhas = useMemo(() => {
    const profQuery = histFiltroProfissao.trim().toLowerCase()

    const resolveCategoriaLabel = (row: CctBuscarDadosResultado): string => {
      let cod: string | null = null
      if (row.convencao_ids?.length) {
        for (const id of row.convencao_ids) {
          const found = categoriaPorConvencaoId.get(id)
          if (found) {
            cod = found
            break
          }
        }
      }
      if (!cod) cod = categoriaPorIdScgp.get(row.id_scgp) ?? null
      if (!cod) return '—'
      const nome = categoriasMap.get(cod)
      if (!nome || nome === '—') return cod
      return `${cod} — ${nome}`
    }

    const resolveStatusConvencao = (row: CctBuscarDadosResultado): string => {
      if (row.convencao_ids?.length) {
        for (const id of row.convencao_ids) {
          const found = statusConvencaoPorConvencaoId.get(id)
          if (found) return found
        }
      }
      return statusConvencaoPorIdScgp.get(row.id_scgp) || '—'
    }

    const linhas: HistoricoLinha[] = []

    for (const row of resultados) {
      if (histFiltroIdScgp.length > 0 && !histFiltroIdScgp.includes(String(row.id_scgp))) {
        continue
      }

      const statusConvencao = resolveStatusConvencao(row)
      if (
        histFiltroStatus.length > 0 &&
        !histFiltroStatus.some(
          (s) => s.trim().toLowerCase() === statusConvencao.trim().toLowerCase(),
        )
      ) {
        continue
      }

      const categoria = resolveCategoriaLabel(row)
      const profissoes: CctBuscarDadosProfissaoItem[] = parseCctBuscarDadosProfissoes(row.resultado)

      if (profissoes.length === 0) {
        if (profQuery) continue
        linhas.push({
          key: `${row.id}-empty`,
          resultadoId: row.id,
          id_scgp: row.id_scgp,
          arquivo_nome: row.arquivo_nome,
          created_at: row.created_at,
          status: row.status,
          statusConvencao,
          erro: row.erro,
          categoria,
          profissao: '—',
          salarios: [],
        })
        continue
      }

      for (let i = 0; i < profissoes.length; i++) {
        const item = profissoes[i]
        if (profQuery && !item.profissao.toLowerCase().includes(profQuery)) continue
        linhas.push({
          key: `${row.id}-${i}`,
          resultadoId: row.id,
          id_scgp: row.id_scgp,
          arquivo_nome: row.arquivo_nome,
          created_at: row.created_at,
          status: row.status,
          statusConvencao,
          erro: row.erro,
          categoria,
          profissao: item.profissao || '—',
          salarios: item.salarios,
        })
      }
    }

    const getVal = (linha: HistoricoLinha, key: HistoricoSortKey): string | number => {
      switch (key) {
        case 'created_at':
          return linha.created_at || ''
        case 'id_scgp':
          return linha.id_scgp
        case 'arquivo_nome':
          return (linha.arquivo_nome || '').toLowerCase()
        case 'status':
          return (linha.status || '').toLowerCase()
        case 'status_convencao':
          return (linha.statusConvencao || '').toLowerCase()
        case 'categoria':
          return (linha.categoria || '').toLowerCase()
        case 'profissao':
          return (linha.profissao || '').toLowerCase()
        default:
          return ''
      }
    }

    return linhas.sort((a, b) => {
      const aVal = getVal(a, historicoSortBy)
      const bVal = getVal(b, historicoSortBy)
      if (aVal < bVal) return historicoSortOrder === 'asc' ? -1 : 1
      if (aVal > bVal) return historicoSortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [
    resultados,
    histFiltroIdScgp,
    histFiltroStatus,
    histFiltroProfissao,
    historicoSortBy,
    historicoSortOrder,
    categoriaPorIdScgp,
    categoriaPorConvencaoId,
    statusConvencaoPorIdScgp,
    statusConvencaoPorConvencaoId,
    categoriasMap,
  ])

  const historicoTotalPages = Math.ceil(historicoLinhas.length / ITEMS_PER_PAGE) || 1
  const historicoStart = (historicoPage - 1) * ITEMS_PER_PAGE
  const historicoEnd = historicoStart + ITEMS_PER_PAGE
  const historicoPaginado = historicoLinhas.slice(historicoStart, historicoEnd)

  useEffect(() => {
    setHistoricoPage(1)
  }, [histFiltroIdScgp, histFiltroStatus, histFiltroProfissao])

  const diretorioParam = cctBuscarDadosService.getParamValue(
    parameters,
    CCT_BUSCAR_DADOS_PARAM_DIRETORIO,
  )
  const webhookParam = cctBuscarDadosService.getParamValue(
    parameters,
    CCT_BUSCAR_DADOS_PARAM_WEBHOOK,
    '',
  )

  const getNomeCategoria = (cod: string | null): string => {
    if (!cod) return '—'
    const nome = categoriasMap.get(String(cod))
    if (!nome || nome === '—') return String(cod)
    return `${cod} — ${nome}`
  }

  const clearFilters = () => {
    if (processingLockRef.current) return
    setPageError(null)
    setFiltroIdScgp([])
    setFiltroLaboral([])
    setFiltroPatronal([])
    setFiltroCategorias([])
    setFiltroStatuses([])
  }

  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key as ConvencaoSortKey)
      setSortOrder('asc')
    }
    setCurrentPage(1)
  }

  const toggleHistoricoSort = (key: string) => {
    if (historicoSortBy === key) {
      setHistoricoSortOrder(historicoSortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setHistoricoSortBy(key as HistoricoSortKey)
      setHistoricoSortOrder(key === 'created_at' ? 'desc' : 'asc')
    }
    setHistoricoPage(1)
  }

  const clearHistoricoFilters = () => {
    setHistFiltroIdScgp([])
    setHistFiltroStatus([])
    setHistFiltroProfissao('')
  }

  const formatSalarioValores = (salarios: CctBuscarDadosSalario[]): string => {
    if (!salarios.length) return '—'
    const valores = salarios.map((s) => s.valor.trim()).filter(Boolean)
    return valores.length ? valores.join(' | ') : '—'
  }

  const formatSalarioTextos = (salarios: CctBuscarDadosSalario[]): string => {
    if (!salarios.length) return '—'
    const textos = salarios
      .map((s) => [s.vigencia, s.descricao].map((p) => p.trim()).filter(Boolean).join(' · '))
      .filter(Boolean)
    return textos.length ? textos.join(' | ') : '—'
  }

  const exportHistoricoExcel = () => {
    if (historicoLinhas.length === 0 || exportingExcel) return

    const rows = historicoLinhas.map((linha) => {
      const valor = formatSalarioValores(linha.salarios)
      const texto = formatSalarioTextos(linha.salarios)
      return {
        'ID SCGP': linha.id_scgp,
        'Status CCT': linha.statusConvencao === '—' ? '' : linha.statusConvencao,
        Erro: linha.erro || '',
        Categoria: linha.categoria === '—' ? '' : linha.categoria,
        Profissão: linha.profissao === '—' ? '' : linha.profissao,
        Valor: valor === '—' ? '' : valor,
        Texto: texto === '—' ? '' : texto,
      }
    })

    setExportingExcel(true)
    try {
      const dateStamp = new Date().toISOString().slice(0, 10)
      downloadXlsx('Histórico', `cct-buscar-dados-historico-${dateStamp}.xlsx`, rows)
    } finally {
      setExportingExcel(false)
    }
  }

  const handleAuthorizePasta = async () => {
    setAutorizandoPasta(true)
    setPageError(null)
    try {
      if (!isFileSystemAccessSupported()) {
        throw new Error(
          'Este navegador não permite autorizar pastas locais. Use Chrome ou Edge na máquina que enxerga o compartilhamento.',
        )
      }
      await clearAuthorizedRootDirectory()
      setPastaAutorizada(false)
      setPastaNome(null)

      const handle = await authorizeRootDirectory()
      setPastaAutorizada(true)
      setPastaNome(handle.name)
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return
      setPageError(getErrorMessage(err))
    } finally {
      setAutorizandoPasta(false)
    }
  }

  const handleClearPastaAuth = async () => {
    await clearAuthorizedRootDirectory()
    setPastaAutorizada(false)
    setPastaNome(null)
  }

  const updateProcessItem = (
    idScgp: number,
    patch: Partial<CctBuscarDadosProcessItem>,
  ) => {
    setProcessItems((prev) =>
      prev.map((item) => (item.idScgp === idScgp ? { ...item, ...patch } : item)),
    )
  }

  const handleProcessar = async () => {
    if (processingLockRef.current) return

    setPageError(null)

    if (!webhookParam.trim()) {
      setPageError(
        'Configure a URL do webhook n8n na aba Parâmetros (chave webhook_n8n) antes de processar.',
      )
      if (canUpdate) setActiveTab('parametros')
      return
    }

    if (idsParaProcessar.length === 0) {
      setPageError(
        idsJaProcessadosCount > 0
          ? 'Todos os ids SCGP filtrados já constam na tabela de resultados. Nada a enviar.'
          : 'Nenhum id SCGP para processar com os filtros atuais.',
      )
      return
    }

    processingLockRef.current = true
    const runId = ++runIdRef.current
    cancelRef.current = false
    setProcessando(true)

    const isCurrentRun = () => runIdRef.current === runId

    try {
      let root = await getAuthorizedRootDirectory()
      if (!isCurrentRun()) return

      if (root) {
        try {
          await assertValidCctRootDirectory(root)
        } catch (err) {
          if (!isCurrentRun()) return
          await clearAuthorizedRootDirectory()
          setPastaAutorizada(false)
          setPastaNome(null)
          root = null
          setPageError(
            `${getErrorMessage(err)} A autorização anterior foi removida — autorize novamente a pasta correta.`,
          )
          if (canUpdate) setActiveTab('parametros')
          return
        }
      }

      if (!isCurrentRun()) return

      if (!root) {
        try {
          root = await authorizeRootDirectory()
          if (!isCurrentRun()) return
          setPastaAutorizada(true)
          setPastaNome(root.name)
        } catch (err) {
          if (!isCurrentRun()) return
          if (err instanceof DOMException && err.name === 'AbortError') return
          setPageError(
            getErrorMessage(err) ||
              'Autorize a pasta base na aba Parâmetros antes de processar.',
          )
          return
        }
      }

      if (!isCurrentRun()) return

      setProcessItems(
        idsParaProcessar.map(({ idScgp, convencaoIds }) => ({
          idScgp,
          convencaoIds,
          status: 'aguardando' as const,
        })),
      )

      for (const { idScgp, convencaoIds } of idsParaProcessar) {
        if (!isCurrentRun()) return
        if (cancelRef.current) {
          updateProcessItem(idScgp, { status: 'cancelado', message: 'Cancelado pelo usuário' })
          continue
        }

        updateProcessItem(idScgp, { status: 'lendo_pasta', message: null })

        try {
          const lookup = await listZipFilesForIdScgp(root, idScgp)
          if (!isCurrentRun()) return
          const { zips, pastaEncontrada } = lookup

          if (!pastaEncontrada) {
            const amostra = lookup.subpastasVisiveis.slice(0, 12).join(', ')
            const detalhe = [
              `Pasta ${idScgp} não encontrada em "${lookup.rootName}".`,
              'Estrutura esperada: PASTACONVENCAOCOLETIVA/{id_scgp}/*.zip. Remova a autorização e selecione a pasta PASTACONVENCAOCOLETIVA.',
              amostra
                ? `Subpastas visíveis: ${amostra}${lookup.subpastasVisiveis.length > 12 ? '…' : ''}`
                : 'Nenhuma subpasta listada na pasta autorizada.',
            ].join(' ')

            updateProcessItem(idScgp, {
              status: 'pasta_nao_encontrada',
              message: detalhe,
            })
            continue
          }

          if (zips.length === 0) {
            updateProcessItem(idScgp, {
              status: 'sem_zip',
              message: 'Nenhum arquivo .zip na pasta',
            })
            continue
          }

          let lastStatus: CctBuscarDadosProcessItemStatus = 'sucesso'
          let lastMessage: string | null = null
          let lastArquivo: string | null = null

          for (const zip of zips) {
            if (!isCurrentRun()) return
            if (cancelRef.current) {
              lastStatus = 'cancelado'
              lastMessage = 'Cancelado pelo usuário'
              break
            }

            lastArquivo = zip.name
            updateProcessItem(idScgp, {
              status: 'enviando',
              arquivoNome: zip.name,
              message: `Extraindo ${zip.name}…`,
            })

            try {
              const extraido = await extractDocumentoFromZip(zip.file)
              if (!isCurrentRun()) return
              lastArquivo = extraido.fileName
              updateProcessItem(idScgp, {
                status: 'enviando',
                arquivoNome: extraido.fileName,
                message: `Enviando ${extraido.fileName}`,
              })

              await cctBuscarDadosService.sendDocumentoToWebhook(webhookParam, {
                file: extraido.file,
                fileName: extraido.fileName,
                idScgp,
              })
              if (!isCurrentRun()) return

              await logCreate(
                'cct_buscar_dados',
                String(idScgp),
                `Enviou CCT buscar dados id_scgp ${idScgp} (${extraido.fileName}) ao n8n`,
                {
                  id_scgp: idScgp,
                  arquivo: extraido.fileName,
                  arquivo_zip: extraido.zipFileName,
                  convencao_ids: convencaoIds,
                },
              )
              if (!isCurrentRun()) return

              lastStatus = 'sucesso'
              lastMessage = null
            } catch (err) {
              if (!isCurrentRun()) return
              const message = getErrorMessage(err)
              lastStatus = 'erro'
              lastMessage = message

              await logCreate(
                'cct_buscar_dados',
                String(idScgp),
                `Erro ao enviar CCT buscar dados id_scgp ${idScgp} (${zip.name})`,
                { id_scgp: idScgp, arquivo: zip.name, error: message },
              )
            }
          }

          if (!isCurrentRun()) return
          updateProcessItem(idScgp, {
            status: lastStatus,
            arquivoNome: lastArquivo,
            message: lastMessage,
          })
        } catch (err) {
          if (!isCurrentRun()) return
          const message = getErrorMessage(err)
          updateProcessItem(idScgp, { status: 'erro', message })
        }
      }

      if (isCurrentRun()) {
        await loadResultados()
      }
    } finally {
      if (isCurrentRun()) {
        processingLockRef.current = false
        setProcessando(false)
      }
    }
  }

  const handleCancelar = () => {
    cancelRef.current = true
  }

  const openEditParameter = (param: CctBuscarDadosParameter) => {
    setEditingParameter(param)
    setParameterEditValue(param.value)
    setParameterFormError(null)
    setIsParameterModalOpen(true)
  }

  const closeParameterModal = () => {
    if (savingParameter) return
    setIsParameterModalOpen(false)
    setEditingParameter(null)
    setParameterEditValue('')
    setParameterFormError(null)
  }

  const saveParameter = async () => {
    if (!editingParameter || savingParameter) return
    setSavingParameter(true)
    setParameterFormError(null)
    const oldValue = editingParameter.value
    try {
      await cctBuscarDadosService.updateParameter(editingParameter.id, {
        value: parameterEditValue,
      })
      await logUpdate(
        'cct_buscar_dados',
        editingParameter.id,
        `Alterou parâmetro Buscar dados CCT: ${editingParameter.key}`,
        { value: oldValue },
        { value: parameterEditValue },
        { parameter_key: editingParameter.key, context: 'cct_buscar_dados' },
      )

      if (
        editingParameter.key === CCT_BUSCAR_DADOS_PARAM_DIRETORIO &&
        oldValue !== parameterEditValue
      ) {
        await clearAuthorizedRootDirectory()
        setPastaAutorizada(false)
        setPastaNome(null)
      }

      closeParameterModal()
      await loadParameters()
    } catch (err) {
      setParameterFormError(getErrorMessage(err))
    } finally {
      setSavingParameter(false)
    }
  }

  const displayParamValue = (param: CctBuscarDadosParameter): string => {
    const raw = String(param.value ?? '')
    if (param.key === CCT_BUSCAR_DADOS_PARAM_WEBHOOK && !raw.trim()) return '(não configurado)'
    return raw.length > 48 ? `${raw.slice(0, 48)}…` : raw || '—'
  }

  const processSummary = useMemo(() => {
    if (processItems.length === 0) return null
    const total = processItems.length
    const done = processItems.filter((i) =>
      ['sucesso', 'erro', 'pasta_nao_encontrada', 'sem_zip', 'cancelado'].includes(i.status),
    ).length
    const ok = processItems.filter((i) => i.status === 'sucesso').length
    const fail = processItems.filter((i) =>
      ['erro', 'pasta_nao_encontrada', 'sem_zip'].includes(i.status),
    ).length
    return { total, done, ok, fail }
  }, [processItems])

  const renderBuscaTab = () => (
    <div className="space-y-4">
      {pageError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <Text size="sm">{pageError}</Text>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg rounded-lg p-6 text-white">
          <div className="text-center">
            <Text size="3xl" className="text-white font-bold mb-2">
              {convencoesFiltradas.length}
            </Text>
            <Text size="sm" className="text-white">
              Convenções filtradas
            </Text>
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg rounded-lg p-6 text-white">
          <div className="text-center">
            <Text size="3xl" className="text-white font-bold mb-2">
              {idsParaProcessar.length}
            </Text>
            <Text size="sm" className="text-white">
              IDs SCGP para processar
            </Text>
          </div>
        </div>
        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-lg rounded-lg p-6 text-white">
          <div className="text-center">
            <Text size="3xl" className="text-white font-bold mb-2">
              {semIdScgpCount}
            </Text>
            <Text size="sm" className="text-white">
              Sem id SCGP
            </Text>
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-6 text-white">
          <div className="text-center">
            <Text size="3xl" className="text-white font-bold mb-2">
              {processSummary?.ok ?? 0}
            </Text>
            <Text size="sm" className="text-white">
              Sucessos nesta execução
            </Text>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
            Filtros
          </Heading>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                ID SCGP
              </Text>
              <CheckboxMultiSelect
                options={idScgpOptions}
                selected={filtroIdScgp}
                onChange={setFiltroIdScgp}
                placeholder="Todos"
                searchable
                searchPlaceholder="Buscar id SCGP…"
                disabled={processando || loadingConvencoes}
                className="w-full"
              />
            </div>
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                Entidade laboral
              </Text>
              <CheckboxMultiSelect
                options={laboralOptions}
                selected={filtroLaboral}
                onChange={setFiltroLaboral}
                placeholder="Todas"
                searchable
                searchPlaceholder="Buscar CNPJ…"
                disabled={processando || loadingConvencoes}
                className="w-full"
              />
            </div>
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                Entidade patronal
              </Text>
              <CheckboxMultiSelect
                options={patronalOptions}
                selected={filtroPatronal}
                onChange={setFiltroPatronal}
                placeholder="Todas"
                searchable
                searchPlaceholder="Buscar CNPJ…"
                disabled={processando || loadingConvencoes}
                className="w-full"
              />
            </div>
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                Código de categoria
              </Text>
              <CheckboxMultiSelect
                options={categoriaOptions}
                selected={filtroCategorias}
                onChange={setFiltroCategorias}
                placeholder="Todas"
                searchable
                searchPlaceholder="Buscar categoria…"
                disabled={processando || loadingConvencoes}
                className="w-full"
              />
            </div>
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                Status
              </Text>
              <CheckboxMultiSelect
                options={statusOptions}
                selected={filtroStatuses}
                onChange={setFiltroStatuses}
                placeholder="Todos"
                searchable
                searchPlaceholder="Buscar status…"
                disabled={processando || loadingConvencoes}
                className="w-full"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {canInsert && (
              <Button
                variant="solid"
                size="sm"
                onClick={() => void handleProcessar()}
                disabled={processando || loadingConvencoes || idsParaProcessar.length === 0}
              >
                {processando
                  ? 'Processando…'
                  : `Processar (${idsParaProcessar.length} id${idsParaProcessar.length === 1 ? '' : 's'})`}
              </Button>
            )}
            {processando && (
              <Button variant="outline" size="sm" onClick={handleCancelar}>
                Cancelar
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
              disabled={loadingConvencoes || processando}
            >
              Limpar filtros
            </Button>
            {idsJaProcessadosCount > 0 && (
              <Text size="sm" color="muted">
                {idsJaProcessadosCount} id{idsJaProcessadosCount === 1 ? '' : 's'} pulado
                {idsJaProcessadosCount === 1 ? '' : 's'} (já consta
                {idsJaProcessadosCount === 1 ? '' : 'm'} na tabela de resultados)
              </Text>
            )}
          </div>
        </div>
      </div>

      {processSummary && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
              Andamento
            </Heading>
            <Text size="sm" color="muted" className="mt-1">
              {processSummary.done}/{processSummary.total} concluídos
              {processSummary.ok > 0 ? ` · ${processSummary.ok} sucesso` : ''}
              {processSummary.fail > 0 ? ` · ${processSummary.fail} falha` : ''}
            </Text>
          </div>
          <div className="overflow-x-auto max-h-64">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    ID SCGP
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Arquivo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Mensagem
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {processItems.map((item) => (
                  <tr key={item.idScgp} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 text-sm tabular-nums text-gray-900 dark:text-white">
                      {item.idScgp}
                    </td>
                    <td className={`px-4 py-3 text-sm font-medium ${statusProcessClass(item.status)}`}>
                      {statusProcessLabel(item.status)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {item.arquivoNome || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {item.message || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white flex-shrink-0">
              Convenções
            </Heading>
            <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
              {convencoesOrdenadas.length}{' '}
              {convencoesOrdenadas.length === 1 ? 'registro' : 'registros'}
            </span>
          </div>
        </div>

        {loadingConvencoes ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : convencoesOrdenadas.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Text size="sm" color="muted">
              Nenhuma convenção encontrada com os filtros atuais.
            </Text>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <SortableTableHeader
                      sortKey="id_scgp"
                      currentSort={sortBy}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      className="px-4 py-3 text-left"
                    >
                      ID SCGP
                    </SortableTableHeader>
                    <SortableTableHeader
                      sortKey="cnpj_entidade_laboral"
                      currentSort={sortBy}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      className="px-4 py-3 text-left"
                    >
                      Laboral
                    </SortableTableHeader>
                    <SortableTableHeader
                      sortKey="cnpj_entidade_patronal"
                      currentSort={sortBy}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      className="px-4 py-3 text-left"
                    >
                      Patronal
                    </SortableTableHeader>
                    <SortableTableHeader
                      sortKey="cod_categoria"
                      currentSort={sortBy}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      className="px-4 py-3 text-left"
                    >
                      Categoria
                    </SortableTableHeader>
                    <SortableTableHeader
                      sortKey="status"
                      currentSort={sortBy}
                      sortOrder={sortOrder}
                      onSort={toggleSort}
                      className="px-4 py-3 text-left"
                    >
                      Status
                    </SortableTableHeader>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {convencoesPaginadas.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm tabular-nums text-gray-900 dark:text-white whitespace-nowrap">
                        {item.id_scgp ?? (
                          <span className="text-amber-600 dark:text-amber-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {item.cnpj_entidade_laboral || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {item.cnpj_entidade_patronal || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {getNomeCategoria(item.cod_categoria)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {item.status || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              startIndex={startIndex}
              endIndex={endIndex}
              total={convencoesOrdenadas.length}
              onPageChange={setCurrentPage}
              onPrevious={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
              onNext={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
            />
          </>
        )}
      </div>
    </div>
  )

  const renderHistoricoTab = () => (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
            Filtros
          </Heading>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                ID SCGP
              </Text>
              <CheckboxMultiSelect
                options={histIdScgpOptions}
                selected={histFiltroIdScgp}
                onChange={setHistFiltroIdScgp}
                placeholder="Todos"
                searchable
                searchPlaceholder="Buscar id SCGP…"
                disabled={loadingResultados}
                className="w-full"
              />
            </div>
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                Status
              </Text>
              <CheckboxMultiSelect
                options={histStatusOptions}
                selected={histFiltroStatus}
                onChange={setHistFiltroStatus}
                placeholder="Todos"
                searchable
                searchPlaceholder="Buscar status…"
                disabled={loadingResultados}
                className="w-full"
              />
            </div>
            <div>
              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2">
                Profissão
              </Text>
              <Input
                value={histFiltroProfissao}
                onChange={(e) => setHistFiltroProfissao(e.target.value)}
                placeholder="Buscar profissão…"
                disabled={loadingResultados}
                className="w-full"
              />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={clearHistoricoFilters}
              disabled={loadingResultados}
            >
              Limpar filtros
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void loadResultados()}
              disabled={loadingResultados}
              className="inline-flex items-center gap-2"
            >
              <ArrowPathIcon
                className={`h-4 w-4 ${loadingResultados ? 'animate-spin' : ''}`}
                aria-hidden
              />
              Atualizar
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white flex-shrink-0">
              Histórico
            </Heading>
            <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
              {historicoLinhas.length}{' '}
              {historicoLinhas.length === 1 ? 'linha' : 'linhas'}
            </span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={exportHistoricoExcel}
            disabled={loadingResultados || historicoLinhas.length === 0 || exportingExcel}
            title={
              historicoLinhas.length === 0
                ? 'Nenhum registro para exportar'
                : 'Baixar Excel com a relação filtrada'
            }
          >
            {exportingExcel ? (
              <>
                <ArrowPathIcon className="h-4 w-4 mr-1.5 inline animate-spin" aria-hidden />
                Exportando…
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="h-4 w-4 mr-1.5 inline" aria-hidden />
                Baixar Excel
              </>
            )}
          </Button>
        </div>

        {loadingResultados ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : historicoLinhas.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <Text size="sm" color="muted">
              Nenhum resultado encontrado com os filtros atuais.
            </Text>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <SortableTableHeader
                      sortKey="id_scgp"
                      currentSort={historicoSortBy}
                      sortOrder={historicoSortOrder}
                      onSort={toggleHistoricoSort}
                      className="px-4 py-3 text-left"
                    >
                      ID SCGP
                    </SortableTableHeader>
                    <SortableTableHeader
                      sortKey="status_convencao"
                      currentSort={historicoSortBy}
                      sortOrder={historicoSortOrder}
                      onSort={toggleHistoricoSort}
                      className="px-4 py-3 text-left"
                    >
                      Status
                    </SortableTableHeader>
                    <SortableTableHeader
                      sortKey="categoria"
                      currentSort={historicoSortBy}
                      sortOrder={historicoSortOrder}
                      onSort={toggleHistoricoSort}
                      className="px-4 py-3 text-left"
                    >
                      Categoria
                    </SortableTableHeader>
                    <SortableTableHeader
                      sortKey="profissao"
                      currentSort={historicoSortBy}
                      sortOrder={historicoSortOrder}
                      onSort={toggleHistoricoSort}
                      className="px-4 py-3 text-left"
                    >
                      Profissão
                    </SortableTableHeader>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Valor
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Texto
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {historicoPaginado.map((linha) => (
                    <tr
                      key={linha.key}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm tabular-nums text-gray-900 dark:text-white whitespace-nowrap">
                        {linha.id_scgp}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {linha.statusConvencao}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {linha.categoria}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {linha.profissao}
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums text-gray-900 dark:text-white whitespace-nowrap">
                        {formatSalarioValores(linha.salarios)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 max-w-md">
                        {formatSalarioTextos(linha.salarios)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationControls
              currentPage={historicoPage}
              totalPages={historicoTotalPages}
              startIndex={historicoStart}
              endIndex={historicoEnd}
              total={historicoLinhas.length}
              onPageChange={setHistoricoPage}
              onPrevious={() => historicoPage > 1 && setHistoricoPage(historicoPage - 1)}
              onNext={() =>
                historicoPage < historicoTotalPages && setHistoricoPage(historicoPage + 1)
              }
            />
          </>
        )}
      </div>
    </div>
  )

  const renderParametrosTab = () => (
    <div className="space-y-4">
      <Text size="sm" color="muted">
        Configurações do processamento. Edite o valor conforme necessário.
      </Text>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
            Pasta no navegador
          </Heading>
          <Text size="sm" color="muted" className="mb-4 block">
            Estrutura: PASTACONVENCAOCOLETIVA / {'{id_scgp}'} / arquivo.zip
            {diretorioParam ? ` — parâmetro: ${diretorioParam}` : ''}.
            O navegador não abre UNC sozinho: autorize e selecione a pasta
            <strong className="font-semibold text-gray-700 dark:text-gray-200"> PASTACONVENCAOCOLETIVA</strong>.
            Alterar o parâmetro diretório exige nova autorização.
          </Text>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="solid"
              size="sm"
              onClick={() => void handleAuthorizePasta()}
              disabled={autorizandoPasta}
            >
              {autorizandoPasta ? 'Aguardando…' : 'Autorizar pasta neste navegador'}
            </Button>
            {pastaAutorizada && (
              <Button variant="outline" size="sm" onClick={() => void handleClearPastaAuth()}>
                Remover autorização
              </Button>
            )}
            <Text size="sm" color="muted">
              {pastaAutorizada
                ? `Autorizada${pastaNome ? `: ${pastaNome}` : ''}`
                : 'Não autorizada'}
            </Text>
          </div>
        </div>
      </div>

      {loadingParameters ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/30">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Descrição
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Valor
                  </th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {parameters.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      Nenhum parâmetro cadastrado. Execute o script{' '}
                      <code className="text-xs">frontend/sqls/cct_buscar_dados.sql</code> no
                      Supabase.
                    </td>
                  </tr>
                ) : (
                  parameters.map((param) => (
                    <tr key={param.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td
                        className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 max-w-md"
                        title={param.description ?? undefined}
                      >
                        <span className="font-mono text-xs text-gray-400 dark:text-gray-500 block">
                          {param.key}
                        </span>
                        {param.description ?? '—'}
                      </td>
                      <td
                        className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300"
                        title={
                          String(param.value).length > 20 ? String(param.value) : undefined
                        }
                      >
                        {displayParamValue(param)}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {canUpdate && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditParameter(param)}
                          >
                            Editar
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )

  return (
    <>
      <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
        <PermissionGuard
          moduleName={MODULE_NAME}
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                  Acesso Negado
                </Text>
                <Text size="sm" color="muted">
                  Você não tem permissão para acessar esta página.
                </Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                    {moduleTitle}
                  </Heading>
                  <Text size="sm" color="muted" className="mt-1">
                    {moduleDescription}
                  </Text>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Tooltip content="Recarregar convenções do banco sem sair da página">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void loadConvencoes({ silent: true })}
                      disabled={isRefreshing || processando}
                      className="inline-flex items-center gap-2"
                      aria-label="Atualizar dados"
                    >
                      <ArrowPathIcon
                        className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`}
                        aria-hidden
                      />
                      Atualizar dados
                    </Button>
                  </Tooltip>
                  {refreshError && (
                    <Text size="xs" className="text-red-600 dark:text-red-400 max-w-[16rem] text-right">
                      {refreshError}
                    </Text>
                  )}
                </div>
              </div>
            </div>

            <div className="px-6 pt-4 border-b border-gray-200 dark:border-gray-700">
              <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8">
                  {canInsert && (
                    <button
                      type="button"
                      onClick={() => setActiveTab('busca')}
                      className={tabClass(activeTab === 'busca')}
                    >
                      Busca
                      {!loadingConvencoes ? (
                        <span className="ml-1.5 tabular-nums font-semibold">
                          ({convencoesFiltradas.length})
                        </span>
                      ) : null}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setActiveTab('historico')}
                    className={tabClass(activeTab === 'historico')}
                  >
                    Histórico
                    {!loadingResultados ? (
                      <span className="ml-1.5 tabular-nums font-semibold">
                        ({resultados.length})
                      </span>
                    ) : null}
                  </button>
                  {canUpdate && (
                    <button
                      type="button"
                      onClick={() => setActiveTab('parametros')}
                      className={tabClass(activeTab === 'parametros')}
                    >
                      Parâmetros
                    </button>
                  )}
                </nav>
              </div>
            </div>

            <div className="p-6">
              {canInsert && activeTab === 'busca' && renderBuscaTab()}
              {activeTab === 'historico' && renderHistoricoTab()}
              {canUpdate && activeTab === 'parametros' && renderParametrosTab()}
            </div>
          </div>
        </PermissionGuard>
      </main>

      {isParameterModalOpen && editingParameter && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={closeParameterModal}
            />
            <div
              className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 pt-5 pb-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                    Editar parâmetro
                  </Heading>
                  <button
                    type="button"
                    onClick={closeParameterModal}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                    disabled={savingParameter}
                  >
                    <span className="sr-only">Fechar</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <Text size="sm" className="text-gray-600 dark:text-gray-400 mb-4 block font-mono">
                  {editingParameter.key}
                </Text>

                {parameterFormError && (
                  <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                    <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
                    <span>{parameterFormError}</span>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                      Valor *
                    </Text>
                    <Input
                      type="text"
                      value={parameterEditValue}
                      onChange={(e) => setParameterEditValue(e.target.value)}
                      className="w-full"
                      disabled={!canUpdate || savingParameter}
                    />
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                      Descrição
                    </Text>
                    <div className="w-full rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
                      {editingParameter.description ?? '—'}
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex justify-end gap-2">
                  <Button variant="outline" onClick={closeParameterModal} disabled={savingParameter}>
                    Cancelar
                  </Button>
                  {canUpdate && (
                    <Button
                      variant="solid"
                      disabled={savingParameter}
                      onClick={() => void saveParameter()}
                    >
                      {savingParameter ? 'Salvando...' : 'Salvar'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default CCTBuscarDados
